// Stampa ESC/POS su stampanti termiche Bluetooth LE (PT-210 e compatibili 58 mm) via Web Bluetooth.
import { resolveImageUrl } from "../context/AppSettingsContext";

const SERVICES = [
  { service: "000018f0-0000-1000-8000-00805f9b34fb", chars: ["00002af1-0000-1000-8000-00805f9b34fb"] },
  { service: "e7810a71-73ae-499d-8c15-faa9aef0c3f2", chars: ["bef8d6c9-9c21-4c9e-b632-bd58c1009f9f"] },
  { service: "49535343-fe7d-4ae5-8fa9-9fafd205e455", chars: ["49535343-8841-43f4-a8d4-ecbe34729bb3", "49535343-1e4d-4bd9-ba61-23c647249616"] },
];
const CHUNK = 64;
const LS_KEY = "kl_thermal_device";
let device = null;
let characteristic = null;
const listeners = new Set();

export const isSupported = () => typeof navigator !== "undefined" && !!navigator.bluetooth;
export const isIOS = () => /iP(hone|ad|od)/.test(navigator.userAgent) && !window.MSStream;
export const isConnected = () => !!(device?.gatt?.connected && characteristic);
export const deviceName = () => device?.name || localStorage.getItem(LS_KEY) || "";
export const onStatus = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const emit = () => listeners.forEach((fn) => fn({ connected: isConnected(), name: deviceName() }));

export async function connect() {
  if (!isSupported()) throw new Error(isIOS() ? "Safari iOS non supporta il Bluetooth web: usa il browser gratuito Bluefy" : "Questo browser non supporta Web Bluetooth: usa Chrome o Edge");
  device = await navigator.bluetooth.requestDevice({ filters: SERVICES.map((s) => ({ services: [s.service] })), optionalServices: SERVICES.map((s) => s.service) });
  device.addEventListener("gattserverdisconnected", () => { characteristic = null; emit(); });
  const server = await device.gatt.connect();
  characteristic = null;
  for (const s of SERVICES) {
    try {
      const svc = await server.getPrimaryService(s.service);
      for (const c of s.chars) {
        try {
          const ch = await svc.getCharacteristic(c);
          if (ch.properties.write || ch.properties.writeWithoutResponse) { characteristic = ch; break; }
        } catch { /* prossima caratteristica */ }
      }
    } catch { /* prossimo servizio */ }
    if (characteristic) break;
  }
  if (!characteristic) { device.gatt.disconnect(); throw new Error("Stampante collegata ma nessun canale di stampa trovato"); }
  localStorage.setItem(LS_KEY, device.name || "Stampante");
  emit();
  return device.name;
}

export function disconnect() { try { device?.gatt?.disconnect(); } catch { /* ignore */ } device = null; characteristic = null; emit(); }

// Ricollegamento rapido all'ultima stampante: silenzioso se il browser espone getDevices(), altrimenti chooser filtrato sul nome
export async function reconnect() {
  const name = localStorage.getItem(LS_KEY);
  if (!name) return connect();
  if (isConnected()) return device.name;
  if (device?.gatt && !device.gatt.connected) {
    try { await device.gatt.connect(); await _bind(); return device.name; } catch { /* riprova sotto */ }
  }
  if (navigator.bluetooth?.getDevices) {
    try {
      const known = (await navigator.bluetooth.getDevices()).find((d) => d.name === name);
      if (known) { device = known; device.addEventListener("gattserverdisconnected", () => { characteristic = null; emit(); }); await device.gatt.connect(); await _bind(); return device.name; }
    } catch { /* fallback al chooser */ }
  }
  try {
    device = await navigator.bluetooth.requestDevice({ filters: [{ name }], optionalServices: SERVICES.map((s) => s.service) });
  } catch (e) {
    if (e?.name === "NotFoundError") return connect();
    throw e;
  }
  device.addEventListener("gattserverdisconnected", () => { characteristic = null; emit(); });
  await device.gatt.connect();
  await _bind();
  return device.name;
}

async function _bind() {
  const server = device.gatt;
  characteristic = null;
  for (const s of SERVICES) {
    try {
      const svc = await server.getPrimaryService(s.service);
      for (const c of s.chars) {
        try { const ch = await svc.getCharacteristic(c); if (ch.properties.write || ch.properties.writeWithoutResponse) { characteristic = ch; break; } } catch { /* prossima */ }
      }
    } catch { /* prossimo servizio */ }
    if (characteristic) break;
  }
  if (!characteristic) { device.gatt.disconnect(); throw new Error("Stampante collegata ma nessun canale di stampa trovato"); }
  localStorage.setItem(LS_KEY, device.name || "Stampante");
  emit();
}

export async function write(bytes) {
  if (!isConnected()) await connect();
  const wor = characteristic.properties.writeWithoutResponse && !characteristic.properties.write;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const part = bytes.slice(i, i + CHUNK);
    if (wor) await characteristic.writeValueWithoutResponse(part); else await characteristic.writeValue(part);
    await new Promise((r) => setTimeout(r, 12));
  }
}

// ---------- Builder ESC/POS ----------
const ESC = 0x1b, GS = 0x1d;
const ascii = (s) => Array.from(String(s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/€/g, "EUR").replace(/[^\x20-\x7e\n]/g, "?"), (ch) => ch.charCodeAt(0));

export class Receipt {
  constructor() { this.b = [ESC, 0x40]; }
  raw(...a) { this.b.push(...a); return this; }
  align(a) { return this.raw(ESC, 0x61, { left: 0, center: 1, right: 2 }[a] ?? 0); }
  bold(on) { return this.raw(ESC, 0x45, on ? 1 : 0); }
  size(w = 1, h = 1) { return this.raw(GS, 0x21, ((w - 1) << 4) | (h - 1)); }
  text(s = "") { return this.raw(...ascii(s), 0x0a); }
  line(ch = "-") { return this.text(ch.repeat(32)); }
  row(l, r) { const left = String(l), right = String(r); const pad = Math.max(1, 32 - left.length - right.length); return this.text(left + " ".repeat(pad) + right); }
  feed(n = 3) { return this.raw(ESC, 0x64, n); }
  barcode128(data) {
    const d = ascii(data);
    return this.raw(GS, 0x68, 70, GS, 0x77, 2, GS, 0x48, 2, GS, 0x66, 0, GS, 0x6b, 73, d.length + 2, 0x7b, 0x42, ...d);
  }
  qr(data, size = 6) {
    const d = ascii(data), len = d.length + 3;
    return this.raw(GS, 0x28, 0x6b, 4, 0, 49, 65, 50, 0, GS, 0x28, 0x6b, 3, 0, 49, 67, size, GS, 0x28, 0x6b, 3, 0, 49, 69, 48,
      GS, 0x28, 0x6b, len & 0xff, (len >> 8) & 0xff, 49, 80, 48, ...d, GS, 0x28, 0x6b, 3, 0, 49, 81, 48);
  }
  cut() { return this.feed(4).raw(GS, 0x56, 66, 0); }
  // Immagine 1-bit già rasterizzata ({width, height, data: righe di byte}) → GS v 0
  raster(img) {
    if (!img) return this;
    const wb = Math.ceil(img.width / 8);
    return this.raw(GS, 0x76, 0x30, 0, wb & 0xff, (wb >> 8) & 0xff, img.height & 0xff, (img.height >> 8) & 0xff, ...img.data);
  }
  bytes() { return new Uint8Array(this.b); }
}

// ---------- Logo: immagine → 1-bit con dithering Floyd–Steinberg ----------
const logoCache = new Map();
export async function loadLogoRaster(url, maxWidth = 200) {
  if (!url || typeof document === "undefined") return null;
  if (logoCache.has(url)) return logoCache.get(url);
  const p = (async () => {
    try {
      const img = await new Promise((res, rej) => { const i = new Image(); i.crossOrigin = "anonymous"; i.onload = () => res(i); i.onerror = rej; i.src = url; });
      const w = Math.min(maxWidth, img.naturalWidth) & ~7 || 8;
      const h = Math.max(1, Math.round(img.naturalHeight * (w / img.naturalWidth)));
      const cv = document.createElement("canvas"); cv.width = w; cv.height = h;
      const ctx = cv.getContext("2d"); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, w, h); ctx.drawImage(img, 0, 0, w, h);
      const px = ctx.getImageData(0, 0, w, h).data;
      const gray = new Float32Array(w * h);
      for (let i = 0; i < w * h; i++) { const a = px[i * 4 + 3] / 255; gray[i] = (0.299 * px[i * 4] + 0.587 * px[i * 4 + 1] + 0.114 * px[i * 4 + 2]) * a + 255 * (1 - a); }
      const data = new Uint8Array((w / 8) * h);
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const i = y * w + x, old = gray[i], nw = old < 128 ? 0 : 255, err = old - nw;
        if (nw === 0) data[y * (w / 8) + (x >> 3)] |= 0x80 >> (x & 7);
        if (x + 1 < w) gray[i + 1] += err * 7 / 16;
        if (y + 1 < h) { if (x > 0) gray[i + w - 1] += err * 3 / 16; gray[i + w] += err * 5 / 16; if (x + 1 < w) gray[i + w + 1] += err / 16; }
      }
      return { width: w, height: h, data };
    } catch { return null; }
  })();
  logoCache.set(url, p);
  return p;
}

const logoUrlOf = (settings, branch) => resolveImageUrl(branch?.logo_url) || resolveImageUrl(settings?.logo_url);

async function header(rc, settings, branch, title) {
  const logo = await loadLogoRaster(logoUrlOf(settings, branch));
  rc.align("center");
  if (logo) rc.raster(logo).text("");
  rc.size(2, 2).bold(true).text(settings?.app_name || "Loyalty Club").bold(false).size(1, 1);
  if (branch?.name && branch.name !== settings?.app_name) rc.text(branch.name);
  if (branch?.address) rc.text(branch.address);
  if (branch?.phone) rc.text(`Tel. ${branch.phone}`);
  if (title) rc.text(title);
  rc.line("=");
  return rc;
}

// Buono ritiro premio (stessi dati dello scontrino HTML lib/pickupReceipt.js)
export async function buildPickupReceipt({ reservation: r, customer, settings, branch }) {
  const rc = new Receipt();
  await header(rc, settings, branch, "BUONO RITIRO PREMIO");
  rc.bold(true).size(1, 2).text(r.reward_name).size(1, 1).bold(false).text("");
  rc.barcode128(r.pickup_code || "").text("").qr(r.pickup_code || "", 6).text("");
  rc.size(2, 2).bold(true).text(r.pickup_code || "").bold(false).size(1, 1).line("=");
  rc.align("left");
  rc.row("Cliente", (customer?.name || r.customer_name || "").slice(0, 22));
  rc.row("Carta", customer?.card_number || r.customer_card || "");
  rc.row("Punti alla consegna", `${r.points_cost} pt`);
  const contrib = Number(r.contribution_eur || 0);
  if (contrib > 0) rc.row("Contributo da pagare", `EUR ${contrib.toFixed(2)}`);
  rc.row("Prenotato il", new Date(r.created_at).toLocaleDateString("it-IT")).line();
  rc.text("Presenta questo buono in cassa:").text("il personale legge il codice,").text("consegna il premio e lo annulla.").text("Vale UNA sola volta.").text("");
  rc.align("center").text(`Stampato il ${new Date().toLocaleString("it-IT")}`).cut();
  return rc.bytes();
}

export async function buildTestReceipt(settings) {
  const rc = new Receipt();
  await header(rc, settings, null, "Stampa di prova");
  rc.align("center").text("Codice a barre CODE128").barcode128("RP-TEST1").text("").text("QR code").qr("RP-TEST1", 6).text("").line();
  rc.align("left").row("Carta", "W254875").row("Punti", "120 pt").row("Data", new Date().toLocaleString("it-IT")).line();
  rc.align("center").text("Se leggi questo, la stampante").text("e collegata correttamente!").cut();
  return rc.bytes();
}

// Scontrino punti della cassa (stessi dati di lib/receipt.js buildReceiptHtml)
export async function buildPointsReceipt({ earn, customer, branch, settings, multiplier = 1, activeCoupons = [], duplicate = false }) {
  const rc = new Receipt();
  if (duplicate) rc.align("center").bold(true).text("*** COPIA ***").bold(false);
  await header(rc, settings, branch, null);
  rc.align("left").bold(true).text(customer?.name || "").bold(false).row(`Carta ${customer?.card_number || ""}`, new Date().toLocaleString("it-IT").slice(0, 16)).line();
  const items = earn?.transaction?.items || [];
  for (const it of items) rc.row(`${it.qty}x ${String(it.name).slice(0, 20)}`, `EUR ${Number(it.subtotal ?? it.qty * it.price).toFixed(2)}`);
  if (items.length) rc.line();
  rc.row("Importo", `EUR ${Number(earn?.original_amount || 0).toFixed(2)}`);
  if (earn?.coupon) rc.row(`Coupon ${earn.coupon.code}`, `-EUR ${Number(earn.coupon.discount_eur).toFixed(2)}`);
  if (earn?.cashback?.used > 0) rc.row("Credito cashback usato", `-EUR ${Number(earn.cashback.used).toFixed(2)}`);
  if (earn?.cashback?.earned > 0) rc.row("Cashback maturato", `+EUR ${Number(earn.cashback.earned).toFixed(2)}`);
  rc.bold(true).size(1, 2).row("TOTALE", `EUR ${Number(earn?.final_amount || 0).toFixed(2)}`).size(1, 1).bold(false).line("=");
  rc.align("center").size(2, 2).bold(true).text(`+${Number(earn?.transaction?.points_delta || 0)} PUNTI`).size(1, 1).bold(false);
  if (multiplier > 1) rc.text(`(${multiplier}x campagna)`);
  rc.text(`Nuovo saldo: ${Number(customer?.points || 0)} punti`);
  if (earn?.cashback?.earned > 0) rc.text(`Saldo cashback: EUR ${Number(earn.cashback.balance).toFixed(2)}`);
  const usedCode = earn?.coupon?.code || "";
  const active = (activeCoupons || []).filter((cp) => cp.status !== "used" && cp.code !== usedCode).slice(0, 8);
  if (active.length) {
    rc.line().align("left").bold(true).text("COUPON ANCORA ATTIVI").bold(false);
    for (const cp of active) rc.row(String(cp.name || cp.code).slice(0, 20), cp.code);
  }
  const footer = (branch?.receipt_footer || "").trim() || (settings?.card_footer || "Grazie e a presto!").trim();
  rc.line().align("center");
  for (const l of footer.split("\n")) rc.text(l.slice(0, 32));
  rc.cut();
  return rc.bytes();
}

// Segnale acustico + vibrazione a stampa riuscita / fallita (parametrizzabile: tutte le attività o solo quelle scelte)
const SOUND_KEY = "kl_thermal_sound";
export const getSoundPrefs = () => { try { return { enabled: true, mode: "all", kinds: { points: true, redeem: true, pickup: true, test: true }, ...JSON.parse(localStorage.getItem(SOUND_KEY) || "{}") }; } catch { return { enabled: true, mode: "all", kinds: { points: true, redeem: true, pickup: true, test: true } }; } };
export const setSoundPrefs = (p) => { localStorage.setItem(SOUND_KEY, JSON.stringify(p)); emit(); };
const soundFor = (kind) => { const p = getSoundPrefs(); return p.enabled && (p.mode === "all" || !!p.kinds?.[kind]); };
function beep(ok) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) {
      const ctx = new Ctx(); const t0 = ctx.currentTime;
      (ok ? [[880, 0, 0.12], [1320, 0.12, 0.2]] : [[220, 0, 0.18], [220, 0.26, 0.18]]).forEach(([f, dt, len]) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = ok ? "sine" : "square"; o.frequency.value = f;
        g.gain.setValueAtTime(0.0001, t0 + dt); g.gain.exponentialRampToValueAtTime(ok ? 0.25 : 0.2, t0 + dt + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, t0 + dt + len);
        o.connect(g); g.connect(ctx.destination); o.start(t0 + dt); o.stop(t0 + dt + len + 0.05);
      });
      setTimeout(() => ctx.close().catch(() => {}), 900);
    }
  } catch { /* audio bloccato */ }
  try { navigator.vibrate?.(ok ? 60 : [200, 80, 200]); } catch { /* iOS */ }
}
export const feedback = (kind, ok) => { if (soundFor(kind)) beep(ok); };
async function _run(kind, fn) {
  try { await fn(); feedback(kind, true); } catch (e) { feedback(kind, false); throw e; }
}

export async function printPickup(opts) { await _run("pickup", async () => write(await buildPickupReceipt(opts))); }
export async function printTest(settings) { await _run("test", async () => write(await buildTestReceipt(settings))); }
export async function printPoints(opts) { await _run("points", async () => write(await buildPointsReceipt(opts))); }

// Ricevuta premio (consegna da prenotazione QR o riscatto in cassa)
export async function buildRewardReceipt({ title = "PREMIO CONSEGNATO", rewardName, customer, pointsDelta, newBalance, contribution = 0, code, settings, branch }) {
  const rc = new Receipt();
  await header(rc, settings, branch, title);
  rc.align("center").bold(true).size(1, 2).text(rewardName || "").size(1, 1).bold(false).text("").align("left");
  rc.row("Cliente", (customer?.name || "").slice(0, 22)).row("Carta", customer?.card_number || "");
  rc.row("Punti scalati", `-${Math.abs(Number(pointsDelta || 0))} pt`);
  if (Number(contribution) > 0) rc.row("Contributo incassato", `EUR ${Number(contribution).toFixed(2)}`);
  if (newBalance != null) rc.bold(true).row("Nuovo saldo", `${Number(newBalance)} pt`).bold(false);
  if (code) rc.row("Codice ritiro", `${code} (annullato)`);
  rc.row("Data", new Date().toLocaleString("it-IT").slice(0, 16)).line();
  rc.align("center").text("Grazie e a presto!").cut();
  return rc.bytes();
}
export async function printReward(opts) { await _run(opts.code ? "pickup" : "redeem", async () => write(await buildRewardReceipt(opts))); }

// Stampa automatica (preferenze di questo dispositivo); parte solo se la stampante è già collegata
const AUTO_KEY = "kl_thermal_auto";
export const getAutoPrint = () => { try { return { points: false, pickup: false, redeem: false, ...JSON.parse(localStorage.getItem(AUTO_KEY) || "{}") }; } catch { return { points: false, pickup: false, redeem: false }; } };
export const setAutoPrint = (prefs) => { localStorage.setItem(AUTO_KEY, JSON.stringify(prefs)); emit(); };
export async function autoPrint(kind, fn) {
  if (!getAutoPrint()[kind] || !isConnected()) return false;
  try { await fn(); return true; } catch (e) { console.warn("auto print failed", e); return false; }
}
